import React, { useState, useEffect } from 'react';
import { Heart, Coins, CreditCard, ArrowRight, RefreshCw, X, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLegalGate } from './LegalAcceptanceModal';
import { API_BASE as API } from '../lib/apiBase';


const PRESETS = [
  { corcheas: 100, label: '100' },
  { corcheas: 500, label: '500' },
  { corcheas: 1000, label: '1K' },
  { corcheas: 2000, label: '2K' },
];

export default function HybridSupportModal({ artist, onClose, onSuccess }) {
  const { token } = useAuth();
  const legalSupport = useLegalGate('support_artist');
  const [method, setMethod] = useState('corcheas');
  const [amount, setAmount] = useState(0);
  const [amountFiat, setAmountFiat] = useState(0);
  const [message, setMessage] = useState('');
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [rate, setRate] = useState(100);

  useEffect(() => {
    const fetchWallet = async () => {
      try {
        const res = await fetch(`${API}/api/wallets/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setBalance(data.balance || 0);
          setRate(data.exchange_rate || 100);
        }
      } catch (e) { console.error(e); }
    };
    fetchWallet();
  }, [token]);

  const corcheasToEur = (c) => (c / rate).toFixed(2);
  const eurToCorcheas = (e) => Math.round(e * rate);

  const handleAmountChange = (corcheas) => {
    setAmount(corcheas);
    setAmountFiat(parseFloat(corcheasToEur(corcheas)));
    setError('');
    setResult(null);
  };

  const handleFiatChange = (eur) => {
    setAmountFiat(eur);
    setAmount(eurToCorcheas(eur));
    setError('');
    setResult(null);
  };

  const handleSupport = async () => {
    if (!legalSupport.gate()) return;
    if (amount <= 0) { setError('Selecciona un monto'); return; }
    setLoading(true);
    setError('');
    try {
      const body = {
        artist_id: artist.id,
        method,
        message,
        currency: 'EUR',
      };
      if (method === 'corcheas') {
        body.amount_corcheas = amount;
      } else {
        body.amount_fiat = amountFiat;
      }

      const res = await fetch(`${API}/api/fiscal/support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || 'Error al enviar apoyo');
      } else if (data.error === 'insufficient_balance') {
        setError(`Corcheas insuficientes. Tienes ${data.balance}, necesitas ${data.needed}.`);
      } else if (data.success) {
        setResult(data);
        if (onSuccess) onSuccess(data);
      }
    } catch (e) {
      setError('Error de conexion');
    }
    setLoading(false);
  };

  const platformFee = Math.round(amount * 0.15);
  const artistReceives = amount - platformFee;

  if (result) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="support-success-modal">
        <div className="bg-slate-800 rounded-2xl p-6 max-w-sm w-full border border-green-500/30 text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={32} className="text-green-400" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Apoyo enviado</h3>
          <p className="text-slate-400 text-sm mb-4">
            {artist?.name} ha recibido <span className="text-green-400 font-bold">{result.net_corcheas} corcheas</span>
            {' '}(~{corcheasToEur(result.net_corcheas)}EUR)
          </p>
          <div className="bg-slate-900/60 rounded-lg p-3 text-xs text-left space-y-1 mb-4">
            <div className="flex justify-between"><span className="text-slate-500">Monto total</span><span className="text-white">{result.amount_corcheas} corcheas</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Pulse Infrastructure Fee (15%)</span><span className="text-amber-300">-{result.fee_corcheas}</span></div>
            <div className="flex justify-between font-bold"><span className="text-slate-400">Artista recibe (85%)</span><span className="text-green-300">{result.net_corcheas} corcheas</span></div>
          </div>
          <button onClick={onClose} className="w-full bg-slate-700 hover:bg-slate-600 text-white py-2.5 rounded-xl transition" data-testid="close-support-modal">
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="hybrid-support-modal">
      {legalSupport.legalModal}
      <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full border border-slate-700/50 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-white transition" data-testid="close-support-btn">
          <X size={20} />
        </button>

        <h3 className="text-lg font-bold text-white mb-1">Apoyar a {artist?.name}</h3>
        <p className="text-xs text-slate-500 mb-5">El artista recibe el 85% de tu apoyo</p>

        {/* Method selector */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          <button
            onClick={() => setMethod('corcheas')}
            className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-medium transition ${
              method === 'corcheas'
                ? 'bg-amber-500/20 border-amber-500/50 text-amber-300'
                : 'bg-slate-900/40 border-slate-700/50 text-slate-400 hover:border-slate-600'
            }`}
            data-testid="method-corcheas"
          >
            <Coins size={16} /> Corcheas
          </button>
          <button
            onClick={() => setMethod('eur')}
            className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-medium transition ${
              method === 'eur'
                ? 'bg-green-500/20 border-green-500/50 text-green-300'
                : 'bg-slate-900/40 border-slate-700/50 text-slate-400 hover:border-slate-600'
            }`}
            data-testid="method-eur"
          >
            <CreditCard size={16} /> EUR directo
          </button>
        </div>

        {/* Balance info */}
        {method === 'corcheas' && (
          <div className="bg-slate-900/40 rounded-lg p-3 mb-4 flex items-center justify-between">
            <span className="text-xs text-slate-400">Tu saldo</span>
            <span className="text-sm font-bold text-amber-300">{balance.toLocaleString()} corcheas</span>
          </div>
        )}

        {/* Presets */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {PRESETS.map(p => (
            <button
              key={p.corcheas}
              onClick={() => handleAmountChange(p.corcheas)}
              className={`py-2 rounded-lg text-sm font-medium border transition ${
                amount === p.corcheas
                  ? 'bg-pulse-primary/20 border-pulse-primary/50 text-pulse-primary'
                  : 'bg-slate-900/40 border-slate-700/50 text-slate-400 hover:border-slate-600'
              }`}
              data-testid={`preset-${p.corcheas}`}
            >
              {p.label}
              <span className="block text-[10px] text-slate-500">{corcheasToEur(p.corcheas)}EUR</span>
            </button>
          ))}
        </div>

        {/* Custom amount */}
        <div className="mb-4">
          {method === 'corcheas' ? (
            <div className="relative">
              <input
                type="number"
                value={amount || ''}
                onChange={e => handleAmountChange(parseInt(e.target.value) || 0)}
                placeholder="Cantidad de corcheas"
                className="w-full bg-slate-900/40 border border-slate-700/50 rounded-xl p-3 pr-20 text-white text-sm"
                data-testid="custom-amount-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                ~{corcheasToEur(amount)}EUR
              </span>
            </div>
          ) : (
            <div className="relative">
              <input
                type="number"
                step="0.01"
                value={amountFiat || ''}
                onChange={e => handleFiatChange(parseFloat(e.target.value) || 0)}
                placeholder="Cantidad en EUR"
                className="w-full bg-slate-900/40 border border-slate-700/50 rounded-xl p-3 pr-24 text-white text-sm"
                data-testid="custom-amount-fiat-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                ~{amount} corcheas
              </span>
            </div>
          )}
        </div>

        {/* Message */}
        <input
          type="text"
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Mensaje de apoyo (opcional)"
          className="w-full bg-slate-900/40 border border-slate-700/50 rounded-xl p-3 text-white text-sm mb-4"
          data-testid="support-message-input"
        />

        {/* Breakdown */}
        {amount > 0 && (
          <div className="bg-slate-900/60 rounded-lg p-3 text-xs space-y-1.5 mb-4" data-testid="support-breakdown">
            <div className="flex justify-between"><span className="text-slate-400">Tu apoyo</span><span className="text-white">{amount} corcheas (~{corcheasToEur(amount)}EUR)</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Pulse Infrastructure Fee (15%)</span><span className="text-slate-500">-{platformFee}</span></div>
            <hr className="border-slate-700" />
            <div className="flex justify-between font-bold"><span className="text-green-400">{artist?.name} recibe (85%)</span><span className="text-green-300">{artistReceives} corcheas</span></div>
          </div>
        )}

        {/* Insufficient balance warning */}
        {method === 'corcheas' && amount > balance && amount > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4 text-xs text-amber-300" data-testid="insufficient-balance-warning">
            <RefreshCw size={12} className="inline mr-1" />
            Corcheas insuficientes. Necesitas recargar {amount - balance} corcheas mas o usa EUR directo.
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-xs text-red-300" data-testid="support-error">
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSupport}
          disabled={loading || amount <= 0 || (method === 'corcheas' && amount > balance)}
          className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="send-support-btn"
        >
          {loading ? (
            <RefreshCw size={16} className="animate-spin" />
          ) : (
            <>
              <Heart size={16} />
              Apoyar con {amount} corcheas
              <ArrowRight size={14} />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
